'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useChart } from '@/contexts/chart-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { InternationalOilPrice } from '@/lib/api/data-contracts';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useEffect, useRef, useState } from 'react';
import { unwrap } from '@/lib/unwrap';

// 1년 전 날짜 계산
const getOneYearAgo = (): string => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0].replace(/-/g, '');
};

// 오늘 날짜
const getToday = (): string => {
    return new Date().toISOString().split('T')[0].replace(/-/g, '');
};

// 국제 유가 색상
const INTERNATIONAL_COLORS: { [key: string]: string } = {
    dubai: '#F39C12',
    brent: '#3498DB',
    wti: '#9B59B6',
};

// 국제 유가 라벨
const INTERNATIONAL_LABELS: { [key: string]: string } = {
    dubai: '두바이유',
    brent: '브렌트유',
    wti: 'WTI',
};

// 연도 변경 구간 plotLines 생성
const getYearChangePlotLines = (data: { priceDate?: string }[]) => {
    const years = new Set<number>();
    const plotLines: any[] = [];

    data.forEach(d => {
        if (d.priceDate) {
            const date = new Date(d.priceDate);
            years.add(date.getFullYear());
        }
    });

    // 각 연도의 1월 1일에 세로선 추가
    years.forEach(year => {
        const yearStart = new Date(year, 0, 1).getTime();
        plotLines.push({
            color: '#888888',
            dashStyle: 'Dash',
            value: yearStart,
            width: 1,
            label: {
                text: `${year}년`,
                align: 'left',
                rotation: 0,
                y: 15,
                style: {
                    color: '#666666',
                    fontSize: '11px',
                },
            },
            zIndex: 5,
        });
    });

    return plotLines;
};

// 최신·전일 행에서 유종별 현재가와 전일 대비 변동을 계산 (sampyo 대시보드와 동일 규칙)
const getLatestPrices = (data: InternationalOilPrice[]) => {
    const sorted = data
        .filter(d => d.priceDate)
        .sort((a, b) => a.priceDate!.localeCompare(b.priceDate!));
    const latest = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];
    if (!latest) return null;
    return {
        lastUpdate: latest.priceDate!,
        items: (['dubai', 'brent', 'wti'] as const)
            .map(key => {
                const price = latest[key];
                const prev = previous?.[key];
                return {
                    key,
                    name: INTERNATIONAL_LABELS[key],
                    price,
                    change: price != null && prev != null ? price - prev : null,
                };
            })
            .filter(item => item.price != null),
    };
};

/**
 * 국제 유가 차트 컴포넌트
 */
export function InternationalOilPriceChart() {
    const { Highcharts, isHighchartsLoaded } = useChart();
    const { toast } = useToast();
    const [internationalData, setInternationalData] = useState<
        InternationalOilPrice[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    const loadData = useCallback(async () => {
        if (!isHighchartsLoaded || !Highcharts) return;

        try {
            setIsLoading(true);

            const startDate = getOneYearAgo();
            const endDate = getToday();

            const response = await apiClient.get('opinet/international-oil-price', {
                searchParams: { startDate, endDate },
            });
            const result = unwrap((await response.json()) as InternationalOilPrice[]);
            setInternationalData(result || []);
        } catch {
            toast({
                title: '국제 유가 데이터 로드 실패',
                description: '국제 유가 데이터를 불러오는 중 오류가 발생했습니다',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [isHighchartsLoaded, Highcharts, toast]);

    useEffect(() => {
        if (isHighchartsLoaded && Highcharts) {
            loadData();
        }
    }, [isHighchartsLoaded, Highcharts, loadData]);

    const chartOptions = {
        chart: {
            type: 'spline',
            zoomType: 'x',
            height: 300,
        },
        title: {
            text: '',
        },
        xAxis: {
            type: 'datetime',
            title: { text: '' },
            dateTimeLabelFormats: {
                month: '%m월',
                year: '%Y년',
            },
            tickInterval: 30 * 24 * 3600 * 1000,
            plotLines: getYearChangePlotLines(internationalData),
        },
        yAxis: {
            title: {
                text: '$/배럴',
            },
            labels: {
                format: '{value:,.0f}',
            },
            startOnTick: false,
            endOnTick: false,
        },
        tooltip: {
            shared: true,
            xDateFormat: '%Y-%m-%d',
            valueSuffix: ' $/배럴',
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
        },
        series: Object.keys(INTERNATIONAL_LABELS).map(key => ({
            name: INTERNATIONAL_LABELS[key],
            color: INTERNATIONAL_COLORS[key],
            data: internationalData
                .filter(
                    d => d.priceDate && d[key as keyof InternationalOilPrice] != null
                )
                .map(d => [
                    new Date(d.priceDate!).getTime(),
                    d[key as keyof InternationalOilPrice] as number,
                ])
                .sort((a, b) => a[0] - b[0]),
        })),
        exporting: { enabled: false },
        credits: { enabled: false },
    };

    const latestPrices = getLatestPrices(internationalData);

    return (
        <Card>
            <CardHeader>
                <CardTitle>국제 유가 동향 (최근 1년)</CardTitle>
            </CardHeader>
            <CardContent>
                {!isLoading && latestPrices && (
                    <div className='mb-3'>
                        <div className='text-right text-xs text-muted-foreground mb-2'>
                            Last update : {latestPrices.lastUpdate}
                        </div>
                        <div className='grid gap-2 sm:grid-cols-3'>
                            {latestPrices.items.map(item => (
                                <div
                                    key={item.key}
                                    className='flex items-center justify-center gap-2.5 rounded border px-3 py-1.5'
                                >
                                    <span className='text-xs font-medium text-muted-foreground'>{item.name}</span>
                                    <span className='text-sm font-semibold'>$ {item.price!.toFixed(2)}</span>
                                    <span
                                        className={`text-xs font-medium ${
                                            item.change == null || item.change === 0
                                                ? 'text-muted-foreground'
                                                : item.change > 0
                                                  ? 'text-red-600'
                                                  : 'text-blue-600'
                                        }`}
                                    >
                                        {item.change == null
                                            ? '-'
                                            : `${item.change > 0 ? '+' : ''}${item.change.toFixed(2)}`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {!isHighchartsLoaded || isLoading ? (
                    <div className='flex items-center justify-center h-[300px]'>
                        <div className='text-gray-500'>
                            {!isHighchartsLoaded
                                ? 'Highcharts 초기화 중...'
                                : '데이터 로딩 중...'}
                        </div>
                    </div>
                ) : internationalData.length === 0 ? (
                    <div className='flex items-center justify-center h-[300px]'>
                        <div className='text-gray-500'>데이터가 없습니다</div>
                    </div>
                ) : (
                    <HighchartsReact
                        ref={chartRef}
                        highcharts={Highcharts}
                        options={chartOptions}
                    />
                )}
            </CardContent>
        </Card>
    );
}
